import { unstable_batchedUpdates } from 'react-dom';
import { notifyManager, QueryClient, QueryObserver, QueryObserverResult } from '@tanstack/react-query';

import { isCreateRootEnabled } from 'utils/reactRoot';

const runUnbatched = <T>(callback: () => T): T => callback();

const batchForLegacyRoots = (): (<T>(callback: () => T) => T) =>
  isCreateRootEnabled() ? runUnbatched : unstable_batchedUpdates;

type ObserverWithResultFactory = { createResult: (...args: unknown[]) => QueryObserverResult };
const observerPrototype = QueryObserver.prototype as unknown as ObserverWithResultFactory;
const createResult = observerPrototype.createResult;
observerPrototype.createResult = function createResultFetchingWhilePaused(this: QueryObserver, ...args) {
  const result = createResult.apply(this, args);
  if (!result.isPaused) return result;
  const { isPending } = result;
  const fetchingWhilePaused = {
    isFetching: true,
    isLoading: isPending,
    isInitialLoading: isPending,
    isRefetching: !isPending,
  };
  return { ...result, ...fetchingWhilePaused } as QueryObserverResult;
};

const CACHE_WRITES = [
  'cancelQueries',
  'removeQueries',
  'setQueriesData',
  'setQueryData',
  'invalidateQueries',
  'refetchQueries',
  'resetQueries',
] as const;

type Method = (...args: unknown[]) => unknown;

/**
 * @deprecated A bridge from react-query v3 behaviour to TanStack Query v5. Don't use it for new clients, and delete
 * each part once its condition is met; the whole helper goes when the last part does. The QueryObserver patch
 * in this module is global from import time, so it also applies to clients built with a plain `new QueryClient()`,
 * tests included; the batching only applies to clients passed to this function.
 *
 * - Batching: both apps mount legacy ReactDOM.render roots unless `feature_flag_react_18_create_root` is on. There,
 *   React renders every setState outside a React event synchronously; v3 batched its notifications itself and v5
 *   leaves it to createRoot, so without this a broad invalidation re-renders once per query and froze TQ. With the
 *   flag on, React batches and this part is a no-op. Remove it with the legacy path of mountReactRoot.
 * - A paused retry still reporting isLoading/isFetching: remove it once components stop treating "not loading and no
 *   error" as "has data".
 */
export function deprecated_withV3Semantics<TClient extends QueryClient>(client: TClient): TClient {
  const batch = batchForLegacyRoots();
  notifyManager.setBatchNotifyFunction(batch);
  for (const cache of [client.getQueryCache(), client.getMutationCache()]) {
    const notify = cache.notify.bind(cache) as Method;
    Object.assign(cache, { notify: (event: unknown) => batch(() => notify(event)) });
  }
  const methods = client as unknown as Record<(typeof CACHE_WRITES)[number], Method>;
  for (const name of CACHE_WRITES) {
    const original = methods[name].bind(client);
    methods[name] = (...args: unknown[]) => batch(() => original(...args));
  }
  return client;
}
